import { useCallback, useMemo } from 'react';
import { groupBy } from 'lodash';
import { useIntl } from 'react-intl';

import { AvailableLineDataRefNode, ReportingTemplateFormula, RowFormula } from '@futrli-sdk/predict';
import { useLocalisedConstantsContext } from '@futrli/localisation/core/localised-constants-context';
import { Consolidation } from '@futrli-sdk/consolidations';

import { useReportBuilderContext } from '../../report-builder.context';
import { ClassifierType, DataField } from '../../types';
import { addOrgCodeToFieldName } from '../../utils-data';
import { PROFIT_LOSS_ID, ROW_FORMULA_ID } from '../../components/row-formula/const';
import { getFormulaMentionsData } from '../../../forecast/prediction-builder/utils';

/**
 * Returns a DataField[] with all of the available row formulas in it
 */
export const useRowFormulas = () => {
  const { rowFormulaData, futrliFormulaData, isTemplate, isWorkspaceTemplate } = useReportBuilderContext();
  const isFutrliTemplate = isTemplate && !isWorkspaceTemplate;

  const intl = useIntl();

  const { nodeStaticIds } = useLocalisedConstantsContext();

  const groupOrder = useMemo(
    () => [
      {
        name: intl.formatMessage({ id: 'financial_fields.category.sales', defaultMessage: 'Sales' }),
        ref: 'sales'
      },
      {
        name: intl.formatMessage({ id: 'financial_fields.category.expenses', defaultMessage: 'Expenses' }),
        ref: 'expenses'
      },
      {
        name: intl.formatMessage({ id: 'financial_fields.category.profit', defaultMessage: 'Profit' }),
        ref: 'profit'
      },
      {
        name: intl.formatMessage({ id: 'financial_fields.type.balance_sheet', defaultMessage: 'Balance Sheet' }),
        ref: 'balance_sheet'
      },
      {
        name: intl.formatMessage({ id: 'financial_fields.type.cash_flow', defaultMessage: 'Cash Flow' }),
        ref: 'cash_flow'
      },
      { name: intl.formatMessage({ id: 'reporting.people', defaultMessage: 'People' }), ref: 'people' },
      { name: intl.formatMessage({ id: 'reporting.ratios', defaultMessage: 'Ratios' }), ref: 'ratios' }
    ],
    [intl]
  );

  /**
   * get rowFormulas grouped by a specific list in a specific order
   */
  const getTransformedRowFormulas = useCallback(
    (
      rowFormulas: (RowFormula | ReportingTemplateFormula)[],
      classifier = 'row-formula',
      lockAll = false
    ): DataField[] => {
      const groupedRowFormulas = groupBy(rowFormulas, 'group');
      const transformedRowFormulas: DataField[] = [];
      for (const group of groupOrder) {
        const groupData = groupedRowFormulas[group.ref];
        if (groupData) {
          transformedRowFormulas.push({
            ref: group.ref === 'balance_sheet' || group.ref === 'cash_flow' ? `${group.ref}_formulas` : group.ref,
            name: group.name,
            disabled: false,
            children: [
              ...groupData
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(g => ({
                  ref: g.ref,
                  name: g.name,
                  disabled: false,
                  children: [],
                  classifier: classifier as ClassifierType,
                  locked: lockAll
                }))
            ],
            locked: lockAll,
            classifier: classifier as ClassifierType
          });
        }
      }

      return transformedRowFormulas;
    },
    [groupOrder]
  );

  /**
   * Filters the given formula data, returning an array of template formulas
   * @param futrliFormulaData - Futrli formula data array
   */
  const getTransformedTemplateFormulas = useCallback(
    (futrliFormulaData: ReportingTemplateFormula[] = []) => {
      return getTransformedRowFormulas(
        futrliFormulaData.filter(f => f.status === 'published'),
        'template-formula',
        true
      );
    },
    [getTransformedRowFormulas]
  );

  /**
   * Takes the given template / row formula data arrays and transforms them into a DataField[]
   * @param futrliFormulaData - Template formula data
   * @param rowFormulaData - Row formula data
   */
  const getTransformedTemplateAndRowFormulas = useCallback(
    (
      futrliFormulaData: ReportingTemplateFormula[] = [],
      rowFormulaData: (ReportingTemplateFormula | RowFormula)[] = []
    ): DataField[] => {
      const formulas = getTransformedRowFormulas(rowFormulaData || []);
      const templateFormulas = getTransformedTemplateFormulas(futrliFormulaData);

      let output: DataField[] = formulas;

      if (templateFormulas.length) {
        output = [
          {
            name: intl.formatMessage({ id: 'reporting.formula_library', defaultMessage: 'Formula library' }),
            ref: 'futrli',
            children: templateFormulas,
            classifier: 'template-formula',
            disabled: false,
            locked: true
          }
        ];
        if (formulas.length) {
          output.push({
            name: intl.formatMessage({
              id: 'reporting.organisation_formulas',
              defaultMessage: 'Organisation formulas'
            }),
            ref: 'organisation',
            children: formulas,
            classifier: 'row-formula',
            disabled: false
          });
        }
      }

      return output;
    },
    [getTransformedRowFormulas, getTransformedTemplateFormulas, intl]
  );

  /**
   * Getting data from mentions menu (showed after pressing @ inside formula editor).
   * Only Profit & Loss and row formulas are offered.
   * @param profitAndLoss
   * @param rowFormulas
   * @param consolidation
   * @returns
   */
  const getAvailableRowFormulaLineData = useCallback(
    (
      profitAndLoss: AvailableLineDataRefNode[],
      rowFormulas: AvailableLineDataRefNode[],
      consolidation?: Consolidation
    ) => {
      const PL = consolidation ? addOrgCodeToFieldName(profitAndLoss as DataField[], consolidation) : profitAndLoss;
      const updatedAvailableLineData = [
        {
          disabled: false,
          ref: PROFIT_LOSS_ID,
          name: intl.formatMessage({ id: 'financial_fields.type.profit_and_loss', defaultMessage: 'Profit & Loss' }),
          children: [...PL],
          classifier: 'static' as ClassifierType
        },
        {
          disabled: false,
          ref: ROW_FORMULA_ID,
          name: intl.formatMessage({ id: 'reporting.row_formulas', defaultMessage: 'Row formulas' }),
          children: [
            ...rowFormulas.map(rf => ({
              name: groupOrder.find(g => g.ref === rf.group)?.name || rf.name,
              disabled: false,
              children: rf.children,
              ref: `${rf.name}-${ROW_FORMULA_ID}`,
              classifier: rf.classifier,
              group: rf.group
            }))
          ],
          classifier: 'static' as ClassifierType
        }
      ];
      const initialOptions = getFormulaMentionsData(
        intl,
        nodeStaticIds,
        updatedAvailableLineData as AvailableLineDataRefNode[]
      );

      const parsedInitialOptions = initialOptions.map(o => ({
        id: o.ref,
        name: o.name,
        depth: o.depth,
        disabled: o.disabled
      }));

      return parsedInitialOptions;
    },
    [intl, nodeStaticIds, groupOrder]
  );

  const rowFormulas = useMemo(() => {
    if (isFutrliTemplate) {
      return getTransformedRowFormulas(rowFormulaData || []);
    } else {
      return getTransformedTemplateAndRowFormulas(futrliFormulaData, rowFormulaData);
    }
  }, [
    rowFormulaData,
    futrliFormulaData,
    isFutrliTemplate,
    getTransformedRowFormulas,
    getTransformedTemplateAndRowFormulas
  ]);

  return {
    rowFormulas,
    getTransformedRowFormulas,
    getTransformedTemplateFormulas,
    getTransformedTemplateAndRowFormulas,
    getAvailableRowFormulaLineData
  };
};
